import React, { useState, useEffect, useCallback } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import { MenuItem } from "@mui/material";
import Swal from "sweetalert2";
import DataTable from "../component/DataTable";
import axiosInstance from "../axiosInterceptor";
import { toast } from "react-toastify";
import AbandonedCheckoutNotificationModal from "../component/AbandonedCheckoutNotificationModal";
import Button from "@mui/material/Button";
import Tooltip from '@mui/material/Tooltip';
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { InputAdornment } from "@mui/material";

const ABANDONED_NOTIFY_TITLE = "Don’t Miss Out! 🛒";
const ABANDONED_NOTIFY_BODY = "Complete your purchase & enjoy the deal today!";

const getCheckoutLinkFromRow = (row) =>
  row?.checkout_link ?? row?.checkoutLink ?? "";

const AbandonedCheckout = () => {

  const [rowsData, setRowsData] = useState([]);
  const [field, setField] = useState("");
  const [headerName, setHeaderName] = useState("");
  const [loading, setLoading] = useState(false);

  // Notification Pop-up
  const [openNotify, setOpenNotify] = useState(false);
  const [selectedAbandonedCheckout, setSelectedAbandonedCheckout] = useState(null);

  const defaultButtons = [
    { id: "btn_1", label: "", link: "", discountCode: "" },
    { id: "btn_2", label: "", link: "", discountCode: "" },
  ];

  const [notifyForm, setNotifyForm] = useState({
    title: "",
    body: "",
    image_url: "",
    link: "",
    buttons: defaultButtons.map((b) => ({
      id: b.id,
      label: "",
      link: "",
      discountCode: "",
    })),
    country: "",
  });

  const [notifyErrors, setNotifyErrors] = useState({
    title: "",
    body: "",
    image_url: "",
    link: "",
    buttons: "",
    country: "",
  });

  const openNotificationModal = (row) => {
    const checkoutLink = getCheckoutLinkFromRow(row);
    setSelectedAbandonedCheckout(row);
    setNotifyForm({
      title: ABANDONED_NOTIFY_TITLE,
      body: ABANDONED_NOTIFY_BODY,
      image_url: "",
      link: checkoutLink,
      buttons: defaultButtons.map((b) =>
        b.id === "btn_1"
          ? { ...b, label: "", link: "" }
          : { ...b, label: "", discountCode: "" }
      ),
      country: "",
    });
    setOpenNotify(true);
  };

  const closeNotificationModal = () => {
    setOpenNotify(false);
    setNotifyForm({
      title: "",
      body: "",
      image_url: "",
      link: "",
      buttons: defaultButtons.map((b) => ({
        id: b.id,
        label: "",
        link: "",
        discountCode: "",
      })),
      country: "",
    });

    setNotifyErrors({
      title: "",
      body: "",
      image_url: "",
      link: "",
      buttons: "",
      country: "",
    });
  };

  const handleSendAbandonedCheckoutPushNotification = async () => {
    let errors = {};

    if (!notifyForm.title.trim()) {
      errors.title = "Title is required";
    }
    if (!notifyForm.body.trim()) {
      errors.body = "Body is required";
    }

    if (
      notifyForm.image_url.trim() &&
      !/^https?:\/\/.+/i.test(notifyForm.image_url)
    ) {
      errors.image_url = "Enter a valid image URL";
    }

    if (
      notifyForm.link.trim() &&
      !/^https?:\/\/.+/i.test(notifyForm.link)
    ) {
      errors.link = "Enter a valid URL for Link";
    }

    const invalidButtonLink = notifyForm.buttons.find((b) => {
      if (b.id !== "btn_1") return false;
      const link = b.link?.trim();
      return link && !/^https?:\/\/.+/i.test(link);
    });
    if (invalidButtonLink) {
      errors.buttons = "Enter a valid URL for Button 1 link";
    }

    setNotifyErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      // Buttons: fixed id (btn_1, btn_2)
      const buttons = notifyForm.buttons.map((b) => {
        const id = b.id;
        const label = b.label?.trim() || "";
        if (id === "btn_2") {
          return {
            id,
            label,
            discountCode: b.discountCode?.trim() || "",
          };
        }
        return {
          id,
          label,
          link: b.link?.trim() || "",
        };
      });

      const basePayload = {
        title: notifyForm.title,
        body: notifyForm.body,
        imageUrl: notifyForm.image_url || null,
        link: notifyForm.link || null,
        buttons,
      };

      const response = await axiosInstance.post(
        "customer/abandonedCheckoutPushNotification",
        {
          ...basePayload,
          email:
            selectedAbandonedCheckout?.email ??
            selectedAbandonedCheckout?.customerEmail ??
            null,
        }
      );

      const { isSuccess, message } = response.data;

      if (isSuccess) {
        Swal.fire("Success!", message, "success");
        fetchData();
      } else {
        Swal.fire("Error!", message || "Notification failed", "error");
      }
    } catch (error) {
      Swal.fire(
        "Error!",
        error?.response?.data?.message || "Something went wrong",
        "error"
      );
    } finally {
      // Close modal regardless of success or failure (keeps UX consistent)
      closeNotificationModal();
    }
  };

  const baseColumns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
    },
    {
      field: "first_name",
      headerName: "First Name",
      flex: 1,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "source",
      headerName: "Source",
      flex: 0.7,
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        return new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }).format(date).replace(/am|pm/i, (m) => m.toUpperCase());
      },
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      flex: 1,
      renderCell: (params) => {
        if (!params.value) return "";

        const date = new Date(params.value);
        if (isNaN(date)) return "Invalid Date";

        return new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
          .format(date)
          .replace(/am|pm/i, (m) => m.toUpperCase());
      },
    },
    {
      field: "push",
      headerName: "Push",
      flex: 0.7,
      sortable: false,
      align: "center",
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={
            <Tooltip title="Push Notification">
              <NotificationsActiveIcon fontSize="small" />
            </Tooltip>}
          onClick={() => openNotificationModal(params.row)}
          sx={{
            textTransform: "none",
            borderRadius: "20px",
            fontSize: "0.75rem",
            padding: "4px 12px",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.08)",
            },
          }}
        >
          Push
        </Button>
      ),
    }
  ];

  const [columns, setColumns] = useState(baseColumns);
  const [totalRecords, setTotalRecords] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [sourceList, setSourceList] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const getSources = async () => {
    try {
      const res = await axiosInstance.get("shopify/getAllSource");

      if (res.data?.isSuccess && Array.isArray(res.data.data)) {
        setSourceList(res.data.data);
      }
    } catch (error) {
      console.error("Source API Error", error);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `shopify/getAllAbandonedCheckouts?page=${paginationModel.page + 1
        }&limit=${paginationModel.pageSize}`;

      const hasFilter =
        !!debouncedSearch || (selectedSource && selectedSource !== "all");

      if (hasFilter) {
        url = `shopify/getCheckoutDataBySearch?page=${paginationModel.page + 1
          }&limit=${paginationModel.pageSize}`;
      }

      if (debouncedSearch) {
        url += `&search=${encodeURIComponent(debouncedSearch)}`;
      }

      if (selectedSource && selectedSource !== "all") {
        url += `&source=${encodeURIComponent(selectedSource)}`;
      }

      const { data } = await axiosInstance.get(url);

      if (data?.data) {
        setRowsData(data.data);
        setTotalRecords(
          hasFilter && typeof data.total === "number"
            ? data.total
            : data.totalRecords
        );
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearch, selectedSource]);

  useEffect(() => {
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
    }));
  }, [debouncedSearch, selectedSource]);

  useEffect(() => {
    fetchData();
    getSources();
  }, [fetchData]);

  const keys = rowsData.length > 0 ? Object.keys(rowsData[0]) : [];

  const handleAddColumn = (e) => {
    e.preventDefault();

    if (!field || !headerName) return;

    const isDuplicate = columns.some((col) => col.field === field);
    if (isDuplicate) {
      Swal.fire("Field already exists!", "", "warning");
      return;
    }

    const newColumn = {
      field: field.trim(),
      headerName: headerName.trim(),
      flex: 1,
    };

    const actionCol = columns.find((col) => col.field === "actions");
    const otherCols = columns.filter((col) => col.field !== "actions");

    setColumns([...otherCols, newColumn, actionCol]);
    setField("");
    setHeaderName("");
  };

  return (
    <div className="content-wrapper pd-top">
      <div className="page-header mt-5">
        <h3 className="page-title">Abandoned Checkouts</h3>
      </div>

      {/* Add Dynamic Column */}
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div style={{ width: "100%" }}>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">
                <h5 className="m-0">Add Dynamic Column</h5>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <form
                onSubmit={handleAddColumn}
                className="p-3 border rounded bg-white"
              >
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label>Field</label>
                      <select
                        className="form-control"
                        value={field}
                        onChange={(e) => setField(e.target.value)}
                      >
                        <option value="" disabled>
                          Select a field
                        </option>
                        {keys.map((key, idx) => (
                          <option key={idx} value={key}>
                            {key}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label>Header Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={headerName}
                        onChange={(e) => setHeaderName(e.target.value)}
                        placeholder="e.g. Email"
                      />
                    </div>
                  </div>
                </div>
                <div className="text-end">
                  <button type="submit" className="btn btn-primary">Add Column</button>
                </div>
              </form>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>

      {/* Search & Source Filter */}
      <div className="mb-4 d-flex">
        <div style={{ flex: 1 }}>
          <TextField
            fullWidth
            style={{ width: "95%" }}
            size="small"
            placeholder="Search abandoned checkouts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "#d0d5dd",
                  borderWidth: "1px",
                },
                "&:hover fieldset": {
                  borderColor: "#1976d2",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976d2",
                  borderWidth: "1px",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#1976d2" }} />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div>
          <TextField
            select
            size="small"
            label="Source"
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            SelectProps={{
              renderValue: (v) =>
                v === "" ? "" : v === "all" ? "All Sources" : v,
              displayEmpty: true,
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 48 * 6,
                  },
                },
              },
            }}
            sx={{
              minWidth: 180,
              backgroundColor: "#fff",
              borderRadius: "8px",
            }}
          >

            <MenuItem value="all">--- All Sources ---</MenuItem>

            {sourceList.map((item, index) => (
              <MenuItem
                key={index}
                value={typeof item === "string" ? item : item.source}
              >
                {typeof item === "string" ? item : item.source}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      {/* Data Table */}
      <div className="row">
        <div className="col-lg-12">
          <DataTable
            key={paginationModel.page}  // 🔥 FORCE REMOUNT
            rows={rowsData}
            columns={columns}
            getRowId={(row) => row._id}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={totalRecords}
            paginationMode="server"
            loading={loading}
            disableVirtualization
            getRowHeight={() => 50}
          />
        </div>
      </div>
      <AbandonedCheckoutNotificationModal
        open={openNotify}
        onClose={closeNotificationModal}
        notifyForm={notifyForm}
        setNotifyForm={setNotifyForm}
        notifyErrors={notifyErrors}
        setNotifyErrors={setNotifyErrors}
        onSend={handleSendAbandonedCheckoutPushNotification}
      />
    </div>
  );
};

export default AbandonedCheckout;
